/**
 * RWBR — REPAIR ROLLS, plus the ledger primitives every incident module shares.
 *
 * RULE (rulebook p.iv, "RESOLVING PROBLEM AND RARE RESULTS"):
 *   "To conduct a 'repair roll,' you need to first find out the 'repair rating'.
 *    Find the driver's PIT CREW grade ... Cross-reference this with the current
 *    portion of the race ... Roll both metallic dice.  If the dice roll is
 *    GREATER than the pit crew's 'repair rating,' the car cannot be repaired,
 *    and is out of the race.  If you roll equal to or LESS than the repair
 *    rating, the pit crew is able to repair the car and get back into the race,
 *    BUT you'll place his driver card at the END of the BOTTOM group."
 *
 * RULE (rulebook p.v, "RESOLVING TROUBLE RESULTS"): the same procedure, read on
 *   the TROUBLE section of the chart instead of the PROBLEM section.
 *
 * RULE (rulebook p.iv): "there are separate REPAIR ROLL charts for TROUBLE and
 *   PROBLEM results.  PROBLEM results require internal/engine repair, while
 *   TROUBLE results ... generally require external/body repair.  Also NOTE that
 *   there are different repair ratings for different portions of the race."
 *
 * RULE (rulebook p.xvi, "TRACK CARD ADJUSTMENTS TO TROUBLE AND REPAIR RANGES"):
 *   "Simply add or subtract the indicated amount from ALL drivers/pit crew
 *    ratings."
 *
 * The 30-cell grid lives in charts.json (`repair-rolls`), transcribed from
 * Board B and cross-verified cell-for-cell against the Board D printing.
 * A `null` cell is the printed dash and means REPAIR IS IMPOSSIBLE.
 *
 * PURITY. Nothing here reads or writes anything outside its arguments, and no
 * argument is mutated. A `Dice` instance is the single exception in spirit: it
 * is the seeded entropy source (D-005) and advances its own internal counter.
 * These functions never *apply* a change — they return the change to apply.
 */

import type { PitCrewGrade } from './types.ts';
import type { Dice } from './dice.ts';
import type { LedgerEvent, Modifier } from './ruleEvent.ts';
import chartsJson from '../data/charts.json';

// ---------------------------------------------------------------------------
// Citations
// ---------------------------------------------------------------------------

export const CITE = {
  repair: 'RB/iv — RESOLVING PROBLEM AND RARE RESULTS (repair roll procedure)',
  repairTrouble: 'RB/v — RESOLVING TROUBLE RESULTS (repair roll on the TROUBLE table)',
  trouble: 'RB/v — RESOLVING TROUBLE RESULTS',
  yellow: 'RB/v — "If one or two cars are involved, there is one YELLOW FLAG turn. If more than two cars are involved, there are two YELLOW FLAG turns."',
  bigOne: 'RB/xv — OPTIONAL AND ADVANCED RULES, "THE BIG ONE"',
  chips: 'RB/xiv — PERFORMANCE CHIPS ("A driver can use a performance chip to avoid trouble." / "a driver CANNOT use a performance chip to avoid a PROBLEM.")',
  chipsBottom: 'RB/xv — "performance chips cannot be carried by drivers in the BOTTOM group."',
  problem: 'RB/iv — "On PROBLEM results, you\'ll roll on the DRIVER FINDER chart first ... and then the PROBLEM FINDER chart."',
  rare: 'RB/iv — "On RARE RESULTS, you\'ll do it the other way around--first you\'ll roll on the RARE RESULTS chart to find out what happened, and then, if necessary, isolate specific drivers."',
  leaderIsTop: 'RB/iv & RB/v — the race LEADER is considered part of the TOP group for PROBLEM, RARE and TROUBLE results.',
  trackAdj: 'RB/xvi — TRACK CARD ADJUSTMENTS TO TROUBLE AND REPAIR RANGES',
  driverFinder: 'DRIVER FINDER charts, PROBLEM RESULTS / RARE RESULTS boards, ©2012 PLAAY Games',
} as const;

// ---------------------------------------------------------------------------
// Ledger primitives (TELEMETRY_SPEC — "the event record")
//
// These used to be declared here. They now live in ruleEvent.ts, which holds the
// single canonical envelope both rule families speak, and are re-exported from
// this module so every existing incident-side import keeps working unchanged.
// ---------------------------------------------------------------------------

export type {
  DiceNotation,
  DiceRecord,
  LedgerEvent,
  Modifier,
  Placement,
  PositionDelta,
} from './ruleEvent.ts';

export type LedgerEventType = 'repair' | 'trouble' | 'problem' | 'rare';

// ---------------------------------------------------------------------------
// Track card adjustments (p.xvi)
// ---------------------------------------------------------------------------

/** As printed on the track cards: "NC", "+1", "-1", "+2", "-2". */
export type TrackAdjustment = 'NC' | '+1' | '-1' | '+2' | '-2' | number | null | undefined;

/** "NC" (no change) and absent both read as 0. */
export function adjustmentValue(adj: TrackAdjustment): number {
  if (adj === null || adj === undefined) return 0;
  if (typeof adj === 'number') {
    if (!Number.isInteger(adj)) throw new RangeError(`track adjustment must be an integer, got ${adj}`);
    return adj;
  }
  const t = adj.trim().toUpperCase();
  if (t === 'NC' || t === '' || t === '0') return 0;
  const n = Number(t);
  if (!Number.isInteger(n)) throw new RangeError(`unrecognised track adjustment "${adj}"`);
  return n;
}

// ---------------------------------------------------------------------------
// Race thirds
// ---------------------------------------------------------------------------

export type RaceThird = 'first' | 'middle' | 'final';
export type RepairTable = 'trouble' | 'problem';

/**
 * Which third of the race a turn falls in.
 *
 * AMBIGUITY: the rulebook names "FIRST 1/3 / MIDDLE 1/3 / FINAL 1/3" (p.iv,
 * Board B) but never states how to round a turn count that does not divide by
 * three. Most defensible reading: split by elapsed fraction, with each third
 * taking its ceiling — turn t of N is in third ceil(3t/N). For N=30 that gives
 * the clean 1-10 / 11-20 / 21-30; for N=25 it gives 1-8 / 9-17 / 18-25.
 *
 * A turn beyond `totalTurns` (legal — "a race cannot end on a YELLOW FLAG
 * turn", p.xi/p.xv, so extra turns are conducted) clamps to the final third.
 */
export function raceThird(turn: number, totalTurns: number): RaceThird {
  if (!Number.isInteger(turn) || turn < 1) {
    throw new RangeError(`turn must be a positive integer, got ${turn}`);
  }
  if (!Number.isInteger(totalTurns) || totalTurns < 1) {
    throw new RangeError(`totalTurns must be a positive integer, got ${totalTurns}`);
  }
  const t = Math.min(turn, totalTurns);
  const index = Math.min(3, Math.max(1, Math.ceil((t * 3) / totalTurns)));
  return index === 1 ? 'first' : index === 2 ? 'middle' : 'final';
}

// ---------------------------------------------------------------------------
// charts.json access
// ---------------------------------------------------------------------------

export interface ChartEffect {
  type: string;
  [key: string]: unknown;
}

export interface PitCrewException {
  grades: PitCrewGrade[];
  outcome: string;
}

export interface ChartEntry {
  text: string;
  effects?: ChartEffect[];
  pitCrewException?: PitCrewException | null;
  footnote?: string;
}

export interface ResultChart {
  id: string;
  name: string;
  sourceCitation: string;
  dice: string;
  entries: Record<string, ChartEntry>;
  notes?: string[];
}

export interface DriverFinderEntry {
  driver: number | 'LEADER';
  half?: 'odd' | 'even';
  text: string;
  footnote?: string;
}

export interface DriverFinderChart {
  id: string;
  name: string;
  sourceCitation: string;
  dice: string;
  entries: Record<string, DriverFinderEntry>;
  notes?: string[];
}

interface RepairChart {
  id: string;
  name: string;
  sourceCitation: string;
  entries: Record<RepairTable, Record<RaceThird, Record<PitCrewGrade, number | null>>>;
  notes?: string[];
}

interface ChartsFile {
  'problem-finder': ResultChart;
  'odd-problem': ResultChart;
  'rare-results-top': ResultChart;
  'rare-results-middle': ResultChart;
  'rare-results-bottom': ResultChart;
  'super-rare-results': ResultChart;
  'black-flag': ResultChart;
  'repair-rolls': RepairChart;
  'driver-finder-top': DriverFinderChart;
  'driver-finder-middle': DriverFinderChart;
  'driver-finder-bottom': DriverFinderChart;
  'board-b-middle-group-layout': Record<string, unknown>;
}

/**
 * The JSON is imported (resolveJsonModule), never read from disk at runtime.
 * The cast is the one place structural JSON meets the typed engine; every
 * shape it asserts is checked by `auditCharts()` and by the test suite.
 */
export const CHARTS = chartsJson as unknown as ChartsFile;

const REPAIR_CHART = CHARTS['repair-rolls'];

export const REPAIR_SOURCE = REPAIR_CHART.sourceCitation;

/** Every printed cell of the REPAIR ROLL grid, unadjusted. `null` = impossible. */
export function baseRepairRating(
  table: RepairTable,
  third: RaceThird,
  grade: PitCrewGrade,
): number | null {
  const byThird = REPAIR_CHART.entries[table];
  if (!byThird) throw new RangeError(`unknown repair table "${table}"`);
  const byGrade = byThird[third];
  if (!byGrade) throw new RangeError(`unknown race third "${third}"`);
  if (!(grade in byGrade)) throw new RangeError(`unknown pit crew grade "${grade}"`);
  return byGrade[grade];
}

export interface RatingLookup {
  /** The printed cell. */
  base: number | null;
  /** The cell after the track card's REPAIR adjustment. `null` = impossible. */
  rating: number | null;
  impossible: boolean;
  modifiers: Modifier[];
}

/**
 * The repair rating actually rolled against.
 *
 * AMBIGUITY: p.xvi says to add/subtract the track adjustment from ALL pit crew
 * ratings, but a printed dash is not a rating — it is the absence of one. A
 * "+2" track therefore does NOT make an impossible cell repairable here. The
 * alternative (treating the dash as 0 and adding to it) would still be
 * unreachable on 2d6 for +1/+2, so the two readings only ever differ if a
 * future track prints a larger adjustment.
 */
export function repairRating(
  table: RepairTable,
  third: RaceThird,
  grade: PitCrewGrade,
  repairAdj: TrackAdjustment = 'NC',
): RatingLookup {
  const base = baseRepairRating(table, third, grade);
  const adj = adjustmentValue(repairAdj);
  const modifiers: Modifier[] = [
    { name: 'repair table', value: table, detail: table === 'problem' ? 'internal / engine damage (RB/iv)' : 'external / body damage (RB/iv)' },
    { name: 'race third', value: third },
    { name: 'pit crew grade', value: grade },
  ];
  if (base === null) {
    modifiers.push({ name: 'printed cell', value: '--', detail: 'repair is impossible for this crew at this stage' });
    return { base, rating: null, impossible: true, modifiers };
  }
  modifiers.push({ name: 'base repair rating', value: base });
  if (adj !== 0) {
    modifiers.push({
      name: 'track card REPAIR adjustment',
      value: adj,
      detail: CITE.trackAdj,
    });
  }
  return { base, rating: base + adj, impossible: false, modifiers };
}

// ---------------------------------------------------------------------------
// The repair roll
// ---------------------------------------------------------------------------

export interface RepairSubject {
  driverId: string;
  pitCrewGrade: PitCrewGrade;
  /** 1-based running order at the moment of the incident. */
  position: number;
  /** Performance chips in hand. Forfeited on the way to the BOTTOM group (p.xv). */
  chips?: number;
  /** For narration only. */
  name?: string;
}

export interface RepairInput {
  car: RepairSubject;
  table: RepairTable;
  turn: number;
  totalTurns: number;
  repairAdj?: TrackAdjustment;
  /**
   * EZRA Chart C's per-trouble-type REPAIR delta, applied ON TOP of the track
   * card's own `repairAdj`. Zero when the Trouble Matrix is off.
   */
  repairBonus?: number;
  dice: Dice;
  /** What put the car in the garage, for the description. */
  cause?: string;
  /**
   * The laps-down optional rule is in force (D-EZ-12). A repaired car goes to
   * the LAPS-DOWN sub-group rather than merely to the end of the BOTTOM group:
   * it lost time in the garage and it lost a lap with it.
   *
   * A FAILED repair is unaffected. RB/iv is explicit that the car cannot be
   * repaired and is out of the race, and an add-on does not overturn the
   * rulebook by implication — see the D-EZ-12 note in `lapsDown.ts`.
   */
  lapsDown?: boolean;
}

export interface RepairResult extends LedgerEvent {
  eventType: 'repair';
  momentTag: 'repair.repaired' | 'repair.failed' | 'repair.impossible';
  table: RepairTable;
  third: RaceThird;
  pitCrewGrade: PitCrewGrade;
  /** The adjusted rating rolled against. `null` = impossible, no roll made. */
  rating: number | null;
  baseRating: number | null;
  /** The 2d6 sum, or null when the cell was impossible and no roll was made. */
  roll: number | null;
  repaired: boolean;
  outOfRace: boolean;
  chipsForfeited: number;
}

/**
 * RB/iv, RB/v. Roll 2d6 SUM. `roll <= rating` = repaired, card to the END of
 * the BOTTOM group. `roll > rating` = out of the race. A null rating (printed
 * dash) is out of the race with no roll at all.
 */
export function resolveRepair(input: RepairInput): RepairResult {
  const { car, table, turn, totalTurns, dice, cause } = input;
  const third = raceThird(turn, totalTurns);
  const lookup = repairRating(table, third, car.pitCrewGrade, input.repairAdj);
  const who = car.name ?? car.driverId;
  const citation = table === 'trouble' ? CITE.repairTrouble : CITE.repair;
  const chips = car.chips ?? 0;
  const causePhrase = cause ? `${cause} — ` : '';

  if (lookup.impossible) {
    return {
      eventType: 'repair',
      momentTag: 'repair.impossible',
      ruleCitation: `${citation}; ${REPAIR_SOURCE}`,
      actorIds: [car.driverId],
      dice: [],
      modifiers: lookup.modifiers,
      description:
        `${causePhrase}the ${table.toUpperCase()} repair chart prints a dash for a grade-${car.pitCrewGrade} ` +
        `crew in the ${third} third: there is no repair to attempt. ${who} is out of the race.`,
      positionDeltas: [{ driverId: car.driverId, from: car.position, to: null, placement: 'out-of-race' }],
      table,
      third,
      pitCrewGrade: car.pitCrewGrade,
      rating: null,
      baseRating: lookup.base,
      roll: null,
      repaired: false,
      outOfRace: true,
      chipsForfeited: chips,
    };
  }

  // Chart C moves the repair rating the same way the track card does. Clamped at
  // 2 because a 2d6 sum can never come in under it, and a rating below 2 would
  // be a silently impossible cell dressed up as a roll.
  const bonus = input.repairBonus ?? 0;
  const rating = bonus === 0 ? (lookup.rating as number) : Math.max(2, (lookup.rating as number) + bonus);
  const roll = dice.sum2d6();
  const repaired = roll.total <= rating;

  return {
    eventType: 'repair',
    momentTag: repaired ? 'repair.repaired' : 'repair.failed',
    ruleCitation: `${citation}; ${REPAIR_SOURCE}`,
    actorIds: [car.driverId],
    dice: [{ kind: '2d6-sum', dice: roll.dice, total: roll.total, label: `${table} repair roll` }],
    modifiers:
      bonus === 0
        ? lookup.modifiers
        : [...lookup.modifiers, { name: 'Chart C REPAIR', value: bonus, detail: 'EZRA-S2 Appendix C, Table C' }],
    description: repaired
      ? `${causePhrase}${who}'s crew needed ${rating} or less and rolled ${roll.total} ` +
        `(${roll.dice[0]}+${roll.dice[1]}). Repaired — ` +
        (input.lapsDown
          ? 'but the time in the garage costs him a lap; card to the LAPS-DOWN sub-group.'
          : 'card to the END of the BOTTOM group.')
      : `${causePhrase}${who}'s crew needed ${rating} or less and rolled ${roll.total} ` +
        `(${roll.dice[0]}+${roll.dice[1]}). The car cannot be repaired; ${who} is out of the race.`,
    positionDeltas: [
      {
        driverId: car.driverId,
        from: car.position,
        to: null,
        placement: repaired ? (input.lapsDown ? 'laps-down' : 'end-of-bottom') : 'out-of-race',
      },
    ],
    table,
    third,
    pitCrewGrade: car.pitCrewGrade,
    rating,
    baseRating: lookup.base,
    roll: roll.total,
    repaired,
    outOfRace: !repaired,
    chipsForfeited: chips,
  };
}

// ---------------------------------------------------------------------------
// Integrity audit — used by the tests, and cheap enough to call at boot
// ---------------------------------------------------------------------------

const CODES_11_66: string[] = (() => {
  const out: string[] = [];
  for (let tens = 1; tens <= 6; tens++) for (let ones = 1; ones <= 6; ones++) out.push(`${tens}${ones}`);
  return out;
})();

/** Every 11-66 code, in order. */
export function codes1166(): string[] {
  return [...CODES_11_66];
}

export function auditCharts(): string[] {
  const problems: string[] = [];
  const elevenSixtySix: (keyof ChartsFile)[] = [
    'problem-finder',
    'odd-problem',
    'rare-results-top',
    'rare-results-middle',
    'rare-results-bottom',
    'super-rare-results',
    'driver-finder-top',
    'driver-finder-middle',
    'driver-finder-bottom',
  ];
  for (const id of elevenSixtySix) {
    const chart = CHARTS[id] as ResultChart | DriverFinderChart;
    const keys = Object.keys(chart.entries).sort();
    if (keys.length !== 36) problems.push(`${id}: expected 36 entries, found ${keys.length}`);
    for (const code of CODES_11_66) {
      if (!(code in chart.entries)) problems.push(`${id}: missing code ${code}`);
    }
  }
  const grades: PitCrewGrade[] = ['A', 'B', 'C', 'D', 'E'];
  for (const table of ['trouble', 'problem'] as RepairTable[]) {
    for (const third of ['first', 'middle', 'final'] as RaceThird[]) {
      for (const grade of grades) {
        const v = baseRepairRating(table, third, grade);
        if (v !== null && (!Number.isInteger(v) || v < 2 || v > 12)) {
          problems.push(`repair-rolls: ${table}/${third}/${grade} = ${v} is not a legal 2d6 target`);
        }
      }
    }
  }
  for (let face = 1; face <= 6; face++) {
    if (!(String(face) in CHARTS['black-flag'].entries)) problems.push(`black-flag: missing face ${face}`);
  }
  return problems;
}
